import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight, Building2, FileText, Users, Link2, ClipboardCheck, PartyPopper, Calculator } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { useTenant } from '../../context/TenantContext';
import api from '../../utils/api';

// Step components
import WelcomeStep from '../../components/onboarding/WelcomeStep';
import FiscalDataStep from '../../components/onboarding/FiscalDataStep';
import BillingDataStep from '../../components/onboarding/BillingDataStep';
import ContactsStep from '../../components/onboarding/ContactsStep';
import AccountingModeStep from '../../components/onboarding/AccountingModeStep';
import HoldedStep from '../../components/onboarding/HoldedStep';
import ReviewStep from '../../components/onboarding/ReviewStep';
import SuccessStep from '../../components/onboarding/SuccessStep';

// Dynamic steps based on accounting mode
const BASE_STEPS = [
  { id: 1, key: 'welcome', title: 'Bienvenida', icon: PartyPopper, description: 'Introduccion al proceso' },
  { id: 2, key: 'fiscal', title: 'Datos Fiscales', icon: Building2, description: 'Informacion fiscal de la empresa' },
  { id: 3, key: 'billing', title: 'Facturacion', icon: FileText, description: 'Configuracion de facturas' },
  { id: 4, key: 'contacts', title: 'Contactos', icon: Users, description: 'Usuarios y contactos' },
  { id: 5, key: 'accounting', title: 'Contabilidad', icon: Calculator, description: 'Modo de gestion contable' },
];

const HOLDED_STEP = { id: 6, key: 'holded', title: 'Holded', icon: Link2, description: 'Integracion con Holded' };

const FINAL_STEPS_HOLDED = [
  { id: 7, key: 'review', title: 'Revision', icon: ClipboardCheck, description: 'Confirmar datos' },
  { id: 8, key: 'success', title: 'Completado', icon: Check, description: 'Incorporacion finalizada' },
];

const FINAL_STEPS_NATIVE = [
  { id: 6, key: 'review', title: 'Revision', icon: ClipboardCheck, description: 'Confirmar datos' },
  { id: 7, key: 'success', title: 'Completado', icon: Check, description: 'Incorporacion finalizada' },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { activeTenant: activeTenantId } = useTenant();
  const [tenantData, setTenantData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    fiscal_data: null,
    billing_data: null,
    contacts_data: null,
    accounting_data: null,
    holded_data: null,
    confirmed: false,
    status: 'not_started',
    progress_percentage: 0,
    completed_steps: [],
    accounting_mode: null
  });

  // Dynamic steps based on accounting mode
  const accountingMode = onboardingData.accounting_mode || onboardingData.accounting_data?.accounting_mode;

  const STEPS = useMemo(() => {
    if (accountingMode === 'holded') {
      return [...BASE_STEPS, HOLDED_STEP, ...FINAL_STEPS_HOLDED];
    }
    return [...BASE_STEPS, ...FINAL_STEPS_NATIVE];
  }, [accountingMode]);

  const totalSteps = STEPS.length;
  const reviewStepId = accountingMode === 'holded' ? 7 : 6;
  const successStepId = accountingMode === 'holded' ? 8 : 7;

  useEffect(() => {
    if (activeTenantId) {
      loadTenantAndOnboarding();
    }
  }, [activeTenantId]);

  const loadTenantAndOnboarding = async () => {
    try {
      setLoading(true);

      // Load tenant data
      const tenantsResponse = await api.get('/tenants');
      const tenant = tenantsResponse.data.find(t => t.id === activeTenantId);
      if (tenant) {
        setTenantData(tenant);
      }

      // Load onboarding status
      const response = await api.get(`/onboarding/${activeTenantId}`);
      setOnboardingData(response.data);

      // Set current step based on status
      if (response.data.status === 'completed' || response.data.status === 'ready_to_operate') {
        const mode = response.data.accounting_mode || response.data.accounting_data?.accounting_mode;
        setCurrentStep(mode === 'holded' ? 8 : 7);
      } else if (response.data.current_step) {
        setCurrentStep(response.data.current_step);
      }
    } catch (error) {
      console.error('Error loading onboarding:', error);
      toast.error('Error al cargar el estado de la incorporacion');
    } finally {
      setLoading(false);
    }
  };

  const saveStepData = async (stepKey, data) => {
    try {
      setSaving(true);

      // Calculate next step based on current step and mode
      let nextStep = currentStep + 1;

      // If we just saved accounting_data and mode is 'native', skip holded step
      if (stepKey === 'accounting_data' && data.accounting_mode === 'native') {
        nextStep = 6; // Go directly to review (step 6 in native mode)
      }

      const payload = {
        [stepKey]: data,
        current_step: nextStep
      };

      const response = await api.put(`/onboarding/${activeTenantId}`, payload);
      setOnboardingData(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error('Error al guardar los datos');
      return { success: false };
    } finally {
      setSaving(false);
    }
  };

  const confirmOnboarding = async () => {
    try {
      setSaving(true);
      const response = await api.put(`/onboarding/${activeTenantId}`, {
        confirmed: true,
        current_step: successStepId
      });
      setOnboardingData(response.data);
      setCurrentStep(successStepId);
      try {
        const submitResponse = await api.post('/onboarding/submit', { tenant_id: activeTenantId });
        const nextUrl = submitResponse.data?.next_url || '/client/onboarding/completado';
        toast.success('Incorporacion enviada para revision');
        navigate(nextUrl);
      } catch (submitError) {
        console.error('Error submitting onboarding:', submitError);
        toast.error('Error al enviar incorporacion para revision');
      }
      return true;
    } catch (error) {
      console.error('Error confirming onboarding:', error);
      toast.error('Error al completar la incorporacion');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async (stepData) => {
    const stepKeyMap = {
      2: 'fiscal_data',
      3: 'billing_data',
      4: 'contacts_data',
      5: 'accounting_data',
      6: accountingMode === 'holded' ? 'holded_data' : null // Only save holded_data if in holded mode
    };

    const stepKey = stepKeyMap[currentStep];

    if (stepKey && stepData) {
      const result = await saveStepData(stepKey, stepData);
      if (!result.success) return;

      // Handle special case: native mode skips holded step
      if (currentStep === 5 && stepData.accounting_mode === 'native') {
        setCurrentStep(6); // Review step in native mode
        return;
      }
    }

    // Handle review confirmation
    if (currentStep === reviewStepId) {
      await confirmOnboarding();
      return;
    }

    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    // Handle special case: going back from review in native mode
    if (currentStep === 6 && accountingMode === 'native') {
      setCurrentStep(5); // Go back to accounting mode step
      return;
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleGoToStep = (stepId) => {
    // In native mode, don't allow going to holded step
    if (accountingMode === 'native' && stepId === 6) {
      return;
    }
    if (stepId <= currentStep || onboardingData.completed_steps.includes(stepId)) {
      setCurrentStep(stepId);
    }
  };

  const getCurrentStepKey = () => {
    const step = STEPS.find(s => s.id === currentStep);
    return step?.key || 'welcome';
  };

  const renderStep = () => {
    const stepProps = {
      data: onboardingData,
      onNext: handleNext,
      onBack: handleBack,
      saving,
      tenantId: activeTenantId
    };

    const stepKey = getCurrentStepKey();

    switch (stepKey) {
      case 'welcome':
        return <WelcomeStep {...stepProps} accountingMode={accountingMode} />;
      case 'fiscal':
        return <FiscalDataStep {...stepProps} initialData={onboardingData.fiscal_data} />;
      case 'billing':
        return <BillingDataStep {...stepProps} initialData={onboardingData.billing_data} />;
      case 'contacts':
        return <ContactsStep {...stepProps} initialData={onboardingData.contacts_data} planId={tenantData?.plan_id} />;
      case 'accounting':
        return <AccountingModeStep {...stepProps} initialData={onboardingData.accounting_data} />;
      case 'holded':
        return <HoldedStep {...stepProps} initialData={onboardingData.holded_data} />;
      case 'review':
        return <ReviewStep {...stepProps} onGoToStep={handleGoToStep} accountingMode={accountingMode} />;
      case 'success':
        return <SuccessStep onGoToDashboard={() => navigate('/client/dashboard')} accountingMode={accountingMode} />;
      default:
        return null;
    }
  };

  if (!activeTenantId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Selecciona un tenant para continuar con la incorporacion.</p>
            <Button onClick={() => navigate('/client/dashboard')} className="mt-4">
              Ir al Panel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando incorporacion...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                Configuracion de {tenantData?.name || 'Empresa'}
              </h1>
              <p className="text-sm text-muted-foreground">
                Completa los siguientes pasos para comenzar a operar
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium">{onboardingData.progress_percentage}% completado</div>
              <Progress value={onboardingData.progress_percentage} className="w-32 h-2 mt-1" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Steps sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {STEPS.map((step) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = onboardingData.completed_steps.includes(step.id) || currentStep > step.id;
                const isClickable = step.id <= currentStep || isCompleted;

                return (
                  <button
                    key={step.id}
                    onClick={() => isClickable && handleGoToStep(step.id)}
                    disabled={!isClickable}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                      isActive
                        ? 'bg-primary text-white shadow-lg'
                        : isCompleted
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : isClickable
                        ? 'bg-white text-slate-600 hover:bg-slate-50'
                        : 'bg-slate-50 text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid={`onboarding-step-${step.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-white/20'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-200'
                    }`}>
                      {isCompleted && !isActive ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Icon className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{step.title}</div>
                      <div className={`text-xs truncate ${isActive ? 'text-white/70' : 'text-slate-500'}`}>
                        {step.description}
                      </div>
                    </div>
                    {isActive && <ChevronRight className="w-4 h-4" />}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main content */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg" data-testid="onboarding-content">
              <CardContent className="p-6 md:p-8">
                {renderStep()}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
